import { memo, useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import * as Sentry from '@sentry/nextjs'

import { Box, Text } from '@chakra-ui/react'
import { useInViewRef } from 'rooks'

import { DeviceFeed } from '@/features/device-feed'
import {
  DRAWER_VIDEO_PLAYER_HEIGHT,
  DRAWER_VIDEO_PLAYER_WIDTH,
  LazyLiveKitVideoPlayer,
  MSEVideoPlayer,
  PlaybackDiagnosticsIProps,
  VideoFeedEvent,
  VideoPlayer,
  formatClipRange,
  useFindDeviceFeed,
  useIncidentVideo,
} from '@/features/video-player'
import { IncidentType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, PlayerEvents, useMixpanel } from '@/utils/analytics'

import {
  ViewToolbar,
  ViewToolbarTabState,
  ViewToolbarView,
} from '../redesigned/ViewToolbar'
import { MixpanelDataIProps } from '../shared/types/types'
import { incidentTypesWithoutVideo } from '../shared/utils/helpers'
import { DarkShowVideoButton } from './DarkShowVideoButton'
import { ShowVideoButton } from './ShowVideoButton'

export interface IncidentVideoIProps {
  incidentId: string
  deviceId: string
  incidentType: IncidentType
  scrollToTop: () => void
  mixpanelData: MixpanelDataIProps
  hasVideoClip: boolean
  /** Incident creation time (ISO) — anchors the extended-clip give-up window. */
  createdAt?: string
  /** When true, start with livestream enabled instead of clip playback */
  defaultToLive?: boolean
  /** Label for clip button when not loading */
  clipLabel?: string
  /** Whether the clip retrieval timeout has expired */
  hasTimedOut: boolean
  /** Called when the user clicks the Clip toggle in the expanded player */
  onClipClick?: () => void
  /**
   * When true, render the view-toolbar (Live / Focused Clip / Extended Clip tabs docked above the
   * video) instead of the in-player Live/Clip toggle. Used by both the legacy and redesigned
   * drawers. The toolbar additionally requires `FeatureFlag.resolvedIncidentClip` — with the flag
   * off, the in-player toggle renders regardless of this prop.
   */
  useViewToolbar?: boolean
}

const IncidentVideoComponent = ({
  incidentId,
  deviceId,
  incidentType,
  scrollToTop,
  mixpanelData,
  hasVideoClip,
  createdAt,
  defaultToLive = false,
  clipLabel,
  hasTimedOut,
  onClipClick,
  useViewToolbar = false,
}: IncidentVideoIProps) => {
  const [isPipActive, setPIPActive] = useState(false)
  const [isLiveStreamEnabled, setLiveStreamEnabled] = useState(defaultToLive)
  const [isExtendedActive, setIsExtendedActive] = useState(false)
  const [videoRef, isInView] = useInViewRef()
  const mode = useLightMode()

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )
  // Feature flag for Lumana cloud WebRTC live view, same gate as Device View's CameraDeviceDrawer.
  const isLumanaStreamEnabled = useFeatureFlag(FeatureFlag.lumanaCameraStream)

  // Clip resolution drives the toolbar's Focused/Extended tabs — without it, fall back to the
  // in-player Live/Clip toggle even when the drawer asks for the toolbar.
  const isResolvedClipEnabled = useFeatureFlag(FeatureFlag.resolvedIncidentClip)
  const shouldShowViewToolbar = useViewToolbar && !!isResolvedClipEnabled

  // Swaps the sticky "Camera view" bar for its dark-package version.
  const isIncidentDrawerRebrand = useFeatureFlag(
    FeatureFlag.incidentDrawerRebrand
  )

  const {
    trackEvent,
    markStreamRequested,
    createPlayerActionHandler,
    createIncidentVideoEventHandler,
    createCameraEventHandler,
  } = useMixpanel(AnalyticsScreen.ActiveIncidentDrawer)

  const handlePlayerAction = createPlayerActionHandler(mixpanelData)
  const handleIncidentVideoEvent = createIncidentVideoEventHandler(mixpanelData)
  const handleCameraEvent = createCameraEventHandler(mixpanelData)

  const {
    availableCamerasIds,
    isDeviceDataLoading,
    errorMessage: feedError,
    nextCamera,
    previousCamera,
    currentCameraId,
    stream: {
      url: cameraFeedUrl,
      isLoading: isFeedDataLoading,
      stream: liveStream,
    },
  } = useFindDeviceFeed(deviceId, handleCameraEvent, isLiveStreamEnabled)

  const {
    url: videoUrl,
    isLoading: isVideoUrlLoading,
    errorMessage: incidentError,
    isProcessing,
    extendedUrl,
    isExtendedClipPending,
    resolvedClipRange,
    extendedClipRange,
    hasFocusedClipFailed,
    hasExtendedClipFailed,
  } = useIncidentVideo({
    hasVideoClip: hasVideoClip,
    incidentId: incidentId,
    incidentType: incidentType,
    createdAt: createdAt,
    onEvent: handleIncidentVideoEvent,
    skip: !hasVideoClip || isDeviceDataLoading,
  })

  const isVideoLoading =
    (isLiveStreamEnabled && isFeedDataLoading) ||
    (!isLiveStreamEnabled && isVideoUrlLoading)

  const disablePIP = () => setPIPActive(false)
  const enablePIP = () => setPIPActive(true)

  const hasExtendedClip = !!extendedUrl
  // When the Extended toggle is active, play the EXTENDED (forensic) clip; otherwise the resolved
  // clip. Falls back to the resolved clip if the extended url is unavailable.
  const clipUrl = isExtendedActive && extendedUrl ? extendedUrl : videoUrl

  // Derived (not state) so it is correct on the same render that live/clip flips. When this was
  // state updated via an effect, switching Live→clip mounted a fresh VideoPlayer with the stale
  // live URL for one render, and react-player would not reload off the live feed. ENG-30186.
  const playerUrl = isLiveStreamEnabled ? cameraFeedUrl : clipUrl

  // Revert to the resolved clip if the extended clip goes away (e.g. a different incident).
  useEffect(() => {
    if (!hasExtendedClip) {
      setIsExtendedActive(false)
    }
  }, [hasExtendedClip])

  useEffect(() => {
    if (isLiveStreamEnabled) {
      markStreamRequested()
      trackEvent(PlayerEvents.toggleLiveFeed, mixpanelData)
    } else {
      trackEvent(PlayerEvents.toggleIncidentClip, mixpanelData)
    }
  }, [isLiveStreamEnabled])

  const handleStartPlaying = () => {
    if (isLiveStreamEnabled) {
      handleCameraEvent(VideoFeedEvent.Playing)
    } else {
      handleIncidentVideoEvent(VideoFeedEvent.Playing)
    }
  }

  const handleClipClick = () => {
    setIsExtendedActive(false)
    onClipClick?.()
  }

  const handleExtendedClick = () => {
    trackEvent(PlayerEvents.toggleExtendedClip, mixpanelData)
    setIsExtendedActive(true)
  }

  // A clip/feed URL was fetched successfully but the <video> element then failed
  // to load or render it (the silent "black player" bug, e.g. PPLD/ENG-21194).
  // Report to Mixpanel for aggregate trends and Sentry for per-user triage. The
  // media_error_label distinguishes a network/firewall block (S3 unreachable)
  // from a decode/driver problem on the machine.
  const reportPlaybackFailure = (
    event: PlayerEvents,
    diagnostics: PlaybackDiagnosticsIProps
  ) => {
    const payload = {
      ...mixpanelData,
      ...diagnostics,
      is_live: isLiveStreamEnabled,
    }
    trackEvent(event, payload)
    Sentry.captureMessage('Incident drawer video playback failure', {
      level: 'warning',
      tags: {
        failure_reason: diagnostics.failure_reason,
        media_error_label: diagnostics.media_error_label,
        clip_region: diagnostics.clip_region ?? 'unknown',
        is_live: String(isLiveStreamEnabled),
      },
      extra: payload,
    })
  }

  const handlePlaybackError = (diagnostics: PlaybackDiagnosticsIProps) =>
    reportPlaybackFailure(PlayerEvents.videoPlaybackError, diagnostics)

  const handlePlaybackStalled = (diagnostics: PlaybackDiagnosticsIProps) =>
    reportPlaybackFailure(PlayerEvents.videoPlaybackStalled, diagnostics)

  // ── View-toolbar (redesigned drawer) tab model ──────────────────────────
  const [visitedViews, setVisitedViews] = useState<
    Record<ViewToolbarView, boolean>
  >({ live: true, focused: false, extended: false })

  const activeView: ViewToolbarView = isLiveStreamEnabled
    ? 'live'
    : isExtendedActive
    ? 'extended'
    : 'focused'

  // Any view actually displayed counts as seen — including the default landing view, which never
  // goes through handleSelectView (leaving the default focused view used to light its "new" dot).
  useEffect(() => {
    setVisitedViews((prev) =>
      prev[activeView] ? prev : { ...prev, [activeView]: true }
    )
  }, [activeView])

  // A new incident gets fresh dot state and never inherits the previous incident's Extended view —
  // its own extended clip is different content the user hasn't chosen (and may not exist yet), and
  // marking it as seen here would suppress its "new" dot forever. Whatever remains on screen (live
  // or focused) still counts as seen.
  useEffect(() => {
    setIsExtendedActive(false)
    setVisitedViews({
      live: isLiveStreamEnabled,
      focused: !isLiveStreamEnabled,
      extended: false,
    })
  }, [incidentId])

  // Wall-clock range of the clip currently shown (focused = resolved clip, extended = forensic
  // clip). Empty on Live or when the active clip carries no times (e.g. Incident.video fallback).
  const activeClipRange =
    activeView === 'extended'
      ? extendedClipRange
      : activeView === 'focused'
      ? resolvedClipRange
      : null
  const clipRangeLabel = formatClipRange(activeClipRange)

  // The pre-clip waiting window counts as loading so the tabs stay consistent with the
  // ClipLoader's toolbar when the user switches to Live before the clip arrives. It runs to the
  // focused-clip failure deadline rather than the ClipLoader's shorter `hasTimedOut`, so there is
  // no window where the tab is greyed out with no explanation before the error state lands.
  const isExpectingVideo = !incidentTypesWithoutVideo.includes(incidentType)
  const isAwaitingClip = isExpectingVideo && !hasFocusedClipFailed

  // Focused = the resolved/ORIGINAL clip (`videoUrl`); loading while it is still being generated,
  // and terminally errored once its failure deadline passes without one.
  const focusedTabState: ViewToolbarTabState = videoUrl
    ? 'ready'
    : hasFocusedClipFailed
    ? 'error'
    : isProcessing || (hasVideoClip && isVideoUrlLoading) || isAwaitingClip
    ? 'loading'
    : 'disabled'

  // Extended = the forensic/EXTENDED clip (`extendedUrl`); loading while clips are still being
  // generated or while the hook is actively polling for the late-arriving forensic clip.
  const extendedTabState: ViewToolbarTabState = extendedUrl
    ? 'ready'
    : hasExtendedClipFailed
    ? 'error'
    : isProcessing || isExtendedClipPending
    ? 'loading'
    : 'disabled'

  const hasFocusedDot =
    focusedTabState === 'ready' &&
    !visitedViews.focused &&
    activeView !== 'focused'
  // The red error dot is the counterpart of the green "new view ready" dot and shares its slot, so
  // it rides the same prop. Unlike the ready dot it isn't gated on visited state — an errored view
  // can never be opened, so it could never be marked seen. Focused failure deliberately has no
  // dot (per design), and it removes the Extended tab entirely anyway.
  const hasExtendedDot =
    extendedTabState === 'error' ||
    (extendedTabState === 'ready' &&
      !visitedViews.extended &&
      activeView !== 'extended')

  const handleSelectView = (view: ViewToolbarView) => {
    if (view === 'live') {
      setLiveStreamEnabled(true)
    } else if (view === 'extended') {
      setLiveStreamEnabled(false)
      handleExtendedClick()
    } else {
      setLiveStreamEnabled(false)
      handleClipClick()
    }
  }

  const shouldRender = hasVideoClip || defaultToLive

  return (
    shouldRender && (
      <>
        {/*
          This box must stay in normal flow so it can scroll fully out of the viewport —
          `isInView` has to go false for the "Camera view" bar below to appear. It used to be
          `position='sticky' top='-352px'` (its height at the time), but the view toolbar and
          clip-range label grew it past 352px, leaving a sliver pinned on screen that kept
          `isInView` true — and when a wrapper box clamped the sticky range, toggling the bar
          moved this box in and out of view in a jumping loop (ENG-31993).
        */}
        <Box
          bg={mode === 'light' ? 'bg.card-elevated.light' : 'bg.card.dark'}
          data-testid='incidentsPage_drawer_incidentVideo'
          pt='10px'
          ref={videoRef}
        >
          <Box px='24px' w='100%'>
            {shouldShowViewToolbar && (
              <ViewToolbar
                activeView={activeView}
                extendedHasDot={hasExtendedDot}
                extendedState={extendedTabState}
                focusedHasDot={hasFocusedDot}
                focusedState={focusedTabState}
                onSelect={handleSelectView}
              />
            )}
            <DeviceFeed
              isLoading={isVideoLoading}
              onClickNext={nextCamera}
              onClickPrevious={previousCamera}
              showCameraSwitcher={availableCamerasIds.length > 1}
            >
              {isLumanaStreamEnabled &&
              isLiveStreamEnabled &&
              liveStream?.kind === 'livekit' ? (
                <LazyLiveKitVideoPlayer
                  apiUrl={liveStream.apiUrl}
                  borderRadius='0'
                  cameraId={currentCameraId}
                  clipLabel={clipLabel}
                  errorLabel={feedError}
                  hasExtendedClip={hasExtendedClip}
                  isClipLoading={!hasVideoClip && !hasTimedOut}
                  isExtendedActive={isExtendedActive}
                  key={activeView}
                  liveOnly={false}
                  onClipClick={handleClipClick}
                  onEvent={handleCameraEvent}
                  onExtendedClick={handleExtendedClick}
                  onPlayerControlAction={handlePlayerAction}
                  onStartPlaying={handleStartPlaying}
                  onToggleLive={setLiveStreamEnabled}
                  placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
                  placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
                  showToggleLiveButton={!shouldShowViewToolbar}
                  token={liveStream.token}
                />
              ) : isMSEStreamingEnabled &&
                isLiveStreamEnabled &&
                currentCameraId ? (
                <MSEVideoPlayer
                  autoplay={true}
                  borderRadius='0'
                  cameraId={currentCameraId}
                  clipLabel={clipLabel}
                  errorLabel={feedError}
                  hideSkipButtons={true}
                  isClipLoading={!hasVideoClip && !hasTimedOut}
                  key={activeView}
                  liveEnabled={isLiveStreamEnabled}
                  liveOnly={false}
                  onClipClick={handleClipClick}
                  onEvent={handleCameraEvent}
                  onPlayerControlAction={handlePlayerAction}
                  onStartPlaying={handleStartPlaying}
                  onToggleLive={setLiveStreamEnabled}
                  placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
                  placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
                  showToggleLiveButton={!shouldShowViewToolbar}
                />
              ) : (
                <VideoPlayer
                  autoplay={true}
                  borderRadius='0'
                  clipLabel={clipLabel}
                  disablePIP={disablePIP}
                  errorLabel={isLiveStreamEnabled ? feedError : incidentError}
                  hasExtendedClip={hasExtendedClip}
                  isClipLoading={!hasVideoClip && !hasTimedOut}
                  isExtendedActive={isExtendedActive}
                  isProcessing={isProcessing}
                  key={activeView}
                  liveEnabled={isLiveStreamEnabled}
                  onClipClick={handleClipClick}
                  onExtendedClick={handleExtendedClick}
                  onPlaybackError={handlePlaybackError}
                  onPlaybackStalled={handlePlaybackStalled}
                  onPlayerControlAction={handlePlayerAction}
                  onStartPlaying={handleStartPlaying}
                  onToggleLive={setLiveStreamEnabled}
                  pipActive={isPipActive}
                  placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
                  placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
                  showToggleLiveButton={!shouldShowViewToolbar}
                  url={playerUrl}
                />
              )}
            </DeviceFeed>
            {shouldShowViewToolbar && clipRangeLabel && (
              <Text
                color={`text.tertiary.${mode}`}
                data-testid='incident-clip-time-range'
                fontSize='11px'
                mt='6px'
              >
                {clipRangeLabel}
              </Text>
            )}
          </Box>
        </Box>
        <Box
          bg={isIncidentDrawerRebrand ? 'transparent' : 'white'}
          boxShadow={isIncidentDrawerRebrand ? 'none' : 'lg'}
          position='sticky'
          top='0'
          zIndex='sticky'
        >
          {isIncidentDrawerRebrand ? (
            <DarkShowVideoButton
              colorScheme={mode}
              disablePIP={disablePIP}
              enablePIP={enablePIP}
              onClick={scrollToTop}
              pipActive={isPipActive}
              show={!!playerUrl && !isInView}
              url={playerUrl}
            />
          ) : (
            <ShowVideoButton
              disablePIP={disablePIP}
              enablePIP={enablePIP}
              onClick={scrollToTop}
              pipActive={isPipActive}
              show={playerUrl && !isInView}
              url={playerUrl}
            />
          )}
        </Box>
      </>
    )
  )
}

const areIncidentVideoPropsEqual = (
  previous: IncidentVideoIProps,
  next: IncidentVideoIProps
) =>
  previous.incidentId === next.incidentId &&
  previous.deviceId === next.deviceId &&
  previous.incidentType === next.incidentType &&
  previous.hasVideoClip === next.hasVideoClip &&
  previous.createdAt === next.createdAt &&
  previous.defaultToLive === next.defaultToLive &&
  previous.clipLabel === next.clipLabel &&
  previous.hasTimedOut === next.hasTimedOut &&
  previous.useViewToolbar === next.useViewToolbar &&
  previous.mixpanelData?.incident_status === next.mixpanelData?.incident_status

export const IncidentVideo = memo(
  IncidentVideoComponent,
  areIncidentVideoPropsEqual
)
